import { useState, useRef } from "react";
import { Upload, X, Loader2, Image as ImageIcon, PackageCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DeliveryConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  packageId: string | null;
  onSuccess: () => void;
}

export const DeliveryConfirmationDialog = ({
  open,
  onOpenChange,
  packageId,
  onSuccess,
}: DeliveryConfirmationDialogProps) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleMarkAsDelivered = async (withPhoto: boolean) => {
    if (!packageId) return;

    setUploading(true);
    try {
      let proofUrl: string | null = null;

      // Upload photo if provided
      if (withPhoto && selectedFile) {
        const fileExt = selectedFile.name.split(".").pop();
        const fileName = `${packageId}/${Date.now()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from("proof-of-delivery")
          .upload(fileName, selectedFile, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) throw uploadError;
        proofUrl = fileName;
      }

      // Update the package with the proof URL and status
      const updateData: { status: string; proof_of_delivery_url?: string } = {
        status: "delivered",
      };

      if (proofUrl) {
        updateData.proof_of_delivery_url = proofUrl;
      }

      const { error: updateError } = await supabase
        .from("packages")
        .update(updateData)
        .eq("id", packageId);

      if (updateError) throw updateError;

      toast.success("Package marked as delivered");
      onSuccess();
      handleClose();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error marking package as delivered:", error);
      }
      toast.error("Failed to update package status");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onOpenChange(false);
  };

  const clearSelection = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5" />
            Mark as Delivered
          </DialogTitle>
          <DialogDescription>
            Confirm the package has been delivered. You can optionally upload a photo as proof.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            id="delivery-confirmation-upload"
          />

          {previewUrl ? (
            <div className="relative">
              <div className="aspect-video rounded-lg overflow-hidden border bg-muted">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-full object-contain"
                />
              </div>
              <Button
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2 h-8 w-8"
                onClick={clearSelection}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <label
              htmlFor="delivery-confirmation-upload"
              className="flex flex-col items-center justify-center aspect-video rounded-lg border-2 border-dashed border-muted-foreground/25 cursor-pointer hover:border-primary/50 transition-colors"
            >
              <ImageIcon className="h-10 w-10 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Click to add delivery proof photo (optional)
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Max file size: 5MB
              </p>
            </label>
          )}
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <div className="flex gap-2 w-full">
            <Button variant="outline" onClick={handleClose} disabled={uploading} className="flex-1">
              Cancel
            </Button>
            {selectedFile ? (
              <Button onClick={() => handleMarkAsDelivered(true)} disabled={uploading} className="flex-1">
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Confirm with Photo
                  </>
                )}
              </Button>
            ) : (
              <Button onClick={() => handleMarkAsDelivered(false)} disabled={uploading} className="flex-1">
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <PackageCheck className="h-4 w-4 mr-2" />
                    Confirm Delivery
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
